import React, { useState } from 'react';
import { MapPin, CheckCircle, XCircle, Locate } from 'lucide-react';
import './PincodeChecker.css';
import { validatePincode, getUserLocation } from '../utils/pincodeValidator';

const PincodeChecker = () => {
  const [pincode, setPincode] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await validatePincode(pincode);
      
      if (result.valid) {
        setCheckResult({
          available: true,
          area: result.area
        });
      } else {
        setCheckResult({
          available: false,
          area: null
        });
      }
    } catch (error) {
      console.error('Error checking pincode:', error);
      setCheckResult({
        available: false,
        area: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUseLocation = async () => {
    setLoading(true);
    try {
      const location = await getUserLocation();
      if (location.pincode) {
        setPincode(location.pincode);
        const result = await validatePincode(location.pincode);
        setCheckResult({
          available: result.valid,
          area: result.area
        });
      } else {
        alert('Could not determine pincode from your location. Please enter manually.');
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      alert('Unable to get your location. Please ensure location access is enabled and try again, or enter pincode manually.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pincode-checker" className="pincode-checker-section">
      <div className="container">
        <div className="section-header">
          <h2 className="heading-2">Check Delivery Availability</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
            Enter your pincode to see if we deliver to your area
          </p>
        </div>
        
        <div className="pincode-checker-container">
          <form onSubmit={handleCheck} className="pincode-form">
            <div className="pincode-input-group">
              <div className="pincode-input-wrapper">
                <MapPin size={20} className="pincode-icon" />
                <input
                  type="text"
                  placeholder="Enter 6-digit pincode"
                  value={pincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setPincode(value);
                    setCheckResult(null);
                  }}
                  className="pincode-input"
                  maxLength="6"
                  required
                />
              </div>
              <button 
                type="button" 
                className="btn-secondary location-btn" 
                onClick={handleUseLocation}
                disabled={loading}
                title="Use my current location"
              >
                <Locate size={18} />
                {loading ? 'Getting...' : 'Use Location'}
              </button>
              <button type="submit" className="btn-primary check-btn" disabled={loading}>
                {loading ? 'Checking...' : 'Check'}
              </button>
            </div>
          </form>

          {checkResult && (
            <div className={`check-result ${checkResult.available ? 'available' : 'unavailable'}`}>
              {checkResult.available ? (
                <>
                  <CheckCircle size={24} className="result-icon" />
                  <div className="result-text">
                    <strong>Great news!</strong> We deliver to {checkResult.area}
                  </div>
                </>
              ) : (
                <>
                  <XCircle size={24} className="result-icon" />
                  <div className="result-text">
                    <strong>Sorry!</strong> We don't deliver to this pincode yet.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PincodeChecker;
